/**
 * LENS 내부 자료형 — Dart struct 의 TS 짝.
 *
 * ★ 이름과 필드는 `generated_code/lib/backend/schema/structs/` 를 그대로 옮겼다.
 *
 * ⚠️⚠️ **`-1` 센티널을 그대로 둔다.** 「0% 가 아니라 데이터 없음」이라는 뜻이고,
 * 이식 1판의 목표는 **숫자가 같은 것**이다. `number | null` 로 바꾸는 것은
 * 이식이 끝난 뒤의 일이다(`docs/LENS-React-이식-범위산정.md` 8절) — 섞으면
 * 다른 숫자가 나왔을 때 **버그인지 의도인지 못 가린다.**
 */

/** 데이터 없음. ⚠️ 0 과 구분되어야 한다. */
export const NO_DATA = -1;

/**
 * 이벤트 한 줄.
 *
 * ⚠️⚠️ **Firestore 게터의 「접힘」이 자료형에 박혀 있다.** 앱은 FF 게터로 읽는데
 * 값이 없으면 `''`/`0`/`false` 로 접힌다. 그래서 대부분이 **nullable 이 아니다** —
 * 없는 것과 비어 있는 것이 애초에 구분되지 않는다.
 *
 * ★ 예외 둘:
 *   `occurredAt`         — `DateTime?` 이다
 *   `narrationValidated` — `hasX()` 로 읽어 **없음(null)과 거짓을 가른다.**
 *                          「검증이 돌고 실패」와 「검증이 안 돎」이 다르기 때문이다.
 */
export interface EventRow {
  eventName: string;
  runId: string;
  campaignId: string;
  chapterId: string;
  sceneId: string;
  contentVersion: string;
  modelVersion: string;
  promptVersion: string;
  appVersion: string;
  occurredAt: Date | null;
  exitReason: string;
  kind: string;
  dc: number;
  rollResult: number;
  success: boolean;
  actionType: string;
  parseOk: boolean;
  attempts: number;
  reason: string;
  regenerated: boolean;
  usedFallback: boolean;
  durationSec: number;
  turnCount: number;
  playerId: string;
  turnIndex: number;
  endingId: string;
  endingKind: string;
  costUsd: number;
  promptTokens: number;
  completionTokens: number;
  durationMs: number;
  fallbackReason: string;
  judgmentKind: string;
  alertLevel: number;
  pursuitMargin: number;
  playerStates: string[];
  sceneConditions: string[];
  sideEffects: string[];
  flagId: string;
  flagKind: string;
  leakedTag: string;
  propId: string;
  rejectedAttempts: number;
  viableApproaches: string[];
  requestedPropId: string;
  /**
   * 콜①이 「행동이 아니다」로 본 까닭 — `'unclear'` | `'impossible'`.
   *
   * ★ 스키마 **1.4.0**(개발 회신 46 1절). `unsupported_action_requested` 에만
   * 실리고 **갈래를 가리지 않는다.**
   *
   * ⚠️⚠️ **빈 문자열이 「없음」이다** — 콜①이 null 이거나 옛 턴이면 키 자체가
   * 없다. 콜①이 안 돈 갈래(안전 차단 · 파싱 실패 · 조회)에도 키가 없다.
   * ★ 그래서 `=== 'unclear'` 로만 쓴다 — **`!== 'unclear'` 는 「없음」까지
   * 끌어들인다**(재현 규칙이 그것을 노린다 · 1-1).
   */
  noActionReason: string;
  /**
   * 이벤트 스키마 판본 — `'1.0.0'` · `'1.4.0'` …
   *
   * ⚠️⚠️ **이벤트는 발행 시점에 굳는다**(개발 회신 41 9절). 새 필드는
   * **옛 회차에 소급되지 않는다.** 그래서 한 창에 판본이 섞이면 어떤
   * 지표는 **새 회차가 쌓이는 동안 천천히 올라가고**, 콘텐츠가 나빠진
   * 것처럼 읽힌다.
   *
   * ★ 지금은 세지 않고 **섞였다는 사실만 배너로 밝힌다**(`lensSchemaNotice`).
   */
  schemaVersion: string;
  /** ⚠️ 유일하게 null 이 뜻을 갖는 필드. */
  narrationValidated: boolean | null;
}

/** 분포 막대 한 줄. */
export interface DistributionItem {
  label: string;
  count: number;
  pct: number;
  valueText: string;
  pctText: string;
  tone: string;
}

/** 장면 통계 한 줄. */
export interface SceneStat {
  sceneId: string;
  chapterId: string;
  enteredText: string;
  completedText: string;
  completionPctText: string;
  completionPct: number;
  avgTurnsText: string;
  dropText: string;
  tone: string;
}

/** 회차 요약 한 줄. */
export interface RunSummary {
  runId: string;
  startedAtText: string;
  durationText: string;
  turnCountText: string;
  endingText: string;
  blockedText: string;
  costText: string;
  tone: string;
}

/** 턴 타임라인 한 줄. */
export interface TurnRow {
  seq: number;
  timeText: string;
  eventLabel: string;
  sceneId: string;
  verb: string;
  detail: string;
  tone: string;
}

// --- Dart 호환 수치 헬퍼 -----------------------------------------------------

/**
 * Dart `num.round()` — **0 에서 멀어지게** 반올림한다.
 *
 * ⚠️ JS `Math.round` 는 **+∞ 쪽으로** 반올림해서 음수 .5 에서 갈린다
 * (실측: Dart `(-2.5).round() == -3`, JS `Math.round(-2.5) === -2`).
 * ★ `toStringAsFixed` 는 양쪽이 완전히 같아 헬퍼가 필요 없다(실측 18개 일치).
 */
export const dartRound = (v: number): number =>
  v < 0 ? -Math.round(-v) : Math.round(v);

/** Dart `toStringAsFixed`. ★ JS `toFixed` 와 동작이 같다 — 이름만 맞춘다. */
export const fixed = (v: number, digits: number): string => v.toFixed(digits);

/** 빈 분포 막대. ★ 0건 줄을 남길 때 쓴다(「분류 막대는 0건인 줄도 남긴다」). */
export const item = (
  label: string,
  count: number,
  pct: number,
  valueText: string,
  pctText: string,
  tone = '',
): DistributionItem => ({ label, count, pct, valueText, pctText, tone });
